import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener } from '@angular/core';
import { Platform } from '@angular/cdk/platform';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { concatMap, filter, takeUntil } from 'rxjs/operators';
import { NavigationEnd } from '@angular/router';
import { environment } from "../environments/environment";
import { NotifService } from './shared/notif.service';
import { NotificationService } from "./backoffice/notification.service";
import { APP_VERSION } from './version';
import { interval, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BackofficeThemeService } from './backoffice/backoffice-theme.service';


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {

  notifications: any[] = [];
  isOnline = true;
  modalVersion = false;
  modalPwaEvent: any;
  modalPwaPlatform: string | undefined;
  displayRepairModal = false;
  localVersion = APP_VERSION;
  serverVersion: string | null = null;

  private destroy$ = new Subject<void>();
  private versionBannerTimeout: any = null;

  constructor(
    private platform: Platform,
    private notifService: NotifService,
    private cdr: ChangeDetectorRef,
    private swUpdate: SwUpdate,
    private notifPush: NotificationService,
    private http: HttpClient,
    private router: Router,
    private backofficeTheme: BackofficeThemeService
  ) {}

  private static readonly DARK_MODE_BACKOFFICE_KEY = 'ui_dark_mode_backoffice';
  private static readonly DARK_MODE_ACCES_CLIENT_KEY = 'ui_dark_mode_acces_client';

  ngOnInit(): void {
    console.log('V' + APP_VERSION);
    console.log(environment);

    this.applySavedDarkMode();
    this.applyThemeForRoute(this.router.url);
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((e) => {
        this.applySavedDarkMode(e.urlAfterRedirects);
        this.applyThemeForRoute(e.urlAfterRedirects);
      });
    this.notifPush.initializeMessageListener();
    this.updateOnlineStatus();

    // Notifications UI
    this.notifService.notification$
      .pipe(takeUntil(this.destroy$))
      .subscribe(notifications => {
        this.notifications = notifications;
        this.cdr.detectChanges();
      });

    // Online/offline
    window.addEventListener('online', () => this.updateOnlineStatus());
    window.addEventListener('offline', () => this.updateOnlineStatus());

    // Service Worker updates
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        concatMap(evt => {
          console.info(`SW Update: ${evt.currentVersion.hash} -> ${evt.latestVersion.hash}`);
          return this.swUpdate.activateUpdate();
        }),
        takeUntil(this.destroy$)
      ).subscribe(() => {
        // Fetch server version for banner display
        this.http.get<any>(`./version.json?t=${Date.now()}`).subscribe({
          next: (res) => {
            if (res.version && res.version !== APP_VERSION) {
              this.serverVersion = res.version;
              this.showVersionBanner();
            }
          },
          error: () => {}
        });
      });

      // Check every 5 min
      interval(300000).pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.swUpdate.checkForUpdate().catch(console.error);
      });

      // Check on tab focus
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.swUpdate.checkForUpdate().catch(console.error);
        }
      });

      this.swUpdate.checkForUpdate().catch(console.error);
    }

    // Version check every 5 min
    interval(300000).pipe(takeUntil(this.destroy$)).subscribe(() => this.checkVersion());

    // PWA install prompt
    this.loadModalPwa();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.versionBannerTimeout) {
      clearTimeout(this.versionBannerTimeout);
    }
  }

  private showVersionBanner(): void {
    // Dans une iframe (ex. modale SAV du backoffice), la bannière est déjà portée par la page hôte
    if (this.versionBannerTimeout || this.modalVersion || window.self !== window.top) return;

    this.versionBannerTimeout = setTimeout(() => {
      if (!this.router.url.startsWith('/iframe/') && !this.router.url.startsWith('/rdv/')) {
        this.modalVersion = true;
        this.cdr.detectChanges();
      }
      this.versionBannerTimeout = null;
    }, 120000); // 2 minutes
  }

  private applySavedDarkMode(url?: string): void {
    const path = (url ?? this.router.url).split('?')[0];
    const key = path.startsWith('/backoffice')
      ? AppComponent.DARK_MODE_BACKOFFICE_KEY
      : path.startsWith('/acces-client')
        ? AppComponent.DARK_MODE_ACCES_CLIENT_KEY
        : null;
    if (key) {
      const saved = localStorage.getItem(key);
      document.documentElement.classList.toggle('app-dark', saved === 'true');
    }
  }

  /** Thème (palettes) : backoffice uniquement ; ailleurs = preset par défaut. */
  private applyThemeForRoute(url: string): void {
    const path = url.split('?')[0];
    if (path.startsWith('/backoffice')) {
      this.backofficeTheme.applyBackofficeThemeFromStorage();
    } else {
      this.backofficeTheme.applyDefaultTheme();
    }
  }

  private updateOnlineStatus(): void {
    this.isOnline = navigator.onLine;
  }

  private checkVersion(): void {
    this.http.get<any>(`./version.json?t=${Date.now()}`).subscribe({
      next: (res) => {
        if (res.version && res.version !== APP_VERSION) {
          this.serverVersion = res.version;
          this.showVersionBanner();
        }
      },
      error: () => {}
    });
  }

  private loadModalPwa(): void {
    if (this.platform.ANDROID || this.platform.isBrowser) {
      window.addEventListener('beforeinstallprompt', (e: any) => {
        e.preventDefault();
        this.modalPwaEvent = e;
        this.modalPwaPlatform = this.platform.ANDROID ? 'ANDROID' : 'isBrowser';
      });
    } else if (this.platform.IOS && this.platform.SAFARI) {
      if (!('standalone' in navigator && (navigator as any).standalone)) {
        this.modalPwaPlatform = 'IOS';
      }
    }
  }

  // Actions publiques
  updateVersion(): void {
    this.modalVersion = false;
    this.cleanCacheandReload();
  }

  closeVersion(): void {
    this.modalVersion = false;
  }

  addToHomeScreen(): void {
    this.modalPwaEvent?.prompt();
    this.modalPwaPlatform = undefined;
  }

  closePwa(): void {
    this.modalPwaPlatform = undefined;
  }

  openRepairModal(): void {
    this.displayRepairModal = true;
  }

  reloadApp(): void {
    location.reload();
  }

  cleanCacheandReload(): void {
    caches.keys()
      .then(keys => Promise.all(keys.map(k => caches.delete(k))))
      .then(() => this.clearIndexedDB())
      .then(() => this.reloadApp());
  }

  private clearIndexedDB(): Promise<void> {
    if (indexedDB.databases) {
      return indexedDB.databases().then(dbs =>
        Promise.all(dbs.map(db => {
          return new Promise<void>(resolve => {
            const req = indexedDB.deleteDatabase(db.name!);
            req.onsuccess = () => resolve();
            req.onerror = () => resolve();
            req.onblocked = () => resolve();
          });
        }))
      ).then(() => {});
    }
    return Promise.resolve();
  }

  hardReset(): void {
    if (confirm("Attention : Cette action peut désactiver les notifications Push temporairement. Continuer ?")) {
      navigator.serviceWorker.getRegistrations()
        .then(regs => Promise.all(regs.map(r => r.unregister())))
        .then(() => caches.keys())
        .then(keys => Promise.all(keys.map(k => caches.delete(k))))
        .then(() => {
          localStorage.clear();
          this.reloadApp();
        });
    }
  }

  reSubscribeToNotifications(): void {
    this.notifPush.subscribeToNotifications(true).subscribe();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'v') {
      event.preventDefault();
      alert(`Version : ${APP_VERSION}`);
    }
  }
}
